import React, { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Pressable, Stack, xcss } from '@atlaskit/primitives';
import Tag from '@atlaskit/tag';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import type { RovoLogo } from '../../constants/icons';
import { ProductKeys, ProductKeys3P } from '../../constants/products';
import type { ThirdPartyConfigsBootstrap } from '../../constants/schemas/3p-config';
import { type OAuthSource } from '../../utils/events/oauth-events';

import { messages } from './messages';

const tileStyles = xcss({
	width: '174px',
	maxWidth: '174px',
	height: '134px',
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderRadius: token('radius.small'),
	borderColor: 'color.border',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	padding: 'space.075',
	overflow: 'hidden',
	boxSizing: 'border-box',
});

const contentStyles = xcss({
	boxSizing: 'border-box',
	justifyContent: 'center',
	alignItems: 'center',
	display: 'flex',
	height: '86px',
});

const iconStyles = xcss({
	width: '32px',
	height: '32px',
	alignItems: 'center',
	justifyContent: 'center',
	display: 'flex',
});

const productTitleStyles = xcss({
	alignItems: 'center',
	maxWidth: '130px',
	boxSizing: 'border-box',
	// Fix height to match titleStyles lineHeight so that inline
	// children with built-in margins (e.g. Tag) do not cause the row to grow taller.
	height: token('space.250'),
});

const titleTextStyles = xcss({
	color: 'color.text',
	font: 'font.body',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	maxWidth: '135px',
});

const newProductTitleTextWidth = xcss({
	maxWidth: '100px',
});

const connectLinkStyles = xcss({
	color: 'color.link',
	font: 'font.body',
	fontStyle: 'normal',
	textDecoration: 'none',
	paddingInlineStart: 'space.100',
	paddingInlineEnd: 'space.150',
	display: 'flex',
	justifyContent: 'center',
	alignContent: 'center',
	':hover': {
		color: 'color.link',
		textDecoration: 'underline',
	},
});

const connectedLinkStyles = xcss({
	color: 'color.text.accent.green',
	font: 'font.body',
	fontStyle: 'normal',
	textDecoration: 'none',
	paddingInlineStart: 'space.100',
	paddingInlineEnd: 'space.150',
});

const errorLinkStyles = xcss({
	color: 'color.link',
	font: 'font.body',
	fontStyle: 'normal',
	textDecoration: 'none',
});

const errorMessageStyles = xcss({
	color: 'color.text.subtlest',
	font: 'font.body',
	fontStyle: 'normal',
	paddingLeft: 'space.025',
	paddingRight: 'space.025',
});

const connectIconStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	paddingLeft: 'space.075',
	paddingRight: 'space.075',
});

const connectPressableStyles = xcss({
	':hover': {
		textDecoration: 'underline',
	},
});

type ConnectStatus = 'default' | 'connected' | 'error';

interface OAuthHandlersProps {
	product: ProductKeys;
	successFlagTitle: React.ReactNode;
	failFlagTitle: React.ReactNode;
	outboundAuthUrl: string;
	onCompletedAction?: () => void;
	onDismissAction?: () => void;
	onFailAction?: () => void;
	isOnSearchDialog?: boolean;
	isBulkConnect?: boolean;
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap;
}
interface OauthHandlerFunctions {
	onAuthorize: (oAuthSource: OAuthSource) => void;
	onDismiss: () => void;
	onFail: (oAuthSource: OAuthSource, error: Error) => void;
}

type useOAuthHandlers = (props: OAuthHandlersProps) => OauthHandlerFunctions;

type ProductTileProps = {
	icon: RovoLogo;
	productName: ReactNode;
	productKey: ProductKeys3P;
	url: string;
	newlyConnected?: boolean;
	useOAuthHandlers: useOAuthHandlers;
	useBulkConnectState: () => {
		productStatuses: Map<ProductKeys3P, boolean>;
		setProductStatus: (productKey: ProductKeys3P, status: boolean) => void;
		setProductConnectedInSession: (status: boolean) => void;
		productOrder: ProductKeys3P[];
		productConnectedInSession: boolean;
	};
	isOnSearchDialog?: boolean;
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap;
	oAuthSource: OAuthSource;
};

export const ProductTile = ({
	icon,
	productName,
	productKey,
	url,
	newlyConnected,
	useOAuthHandlers,
	useBulkConnectState,
	isOnSearchDialog,
	thirdPartyConfigs,
	oAuthSource,
}: ProductTileProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { setProductStatus, setProductConnectedInSession, productStatuses } = useBulkConnectState();
	const [curConnectStatus, setCurConnectStatus] = useState<ConnectStatus>('default');
	const textRef = useRef<HTMLDivElement>(null);
	let productDisplayName =
		productKey !== ProductKeys.ConfluenceDC
			? productName
			: thirdPartyConfigs?.[ProductKeys3P.ConfluenceDC]?.workspaceName;

	useEffect(() => {
		setCurConnectStatus(productStatuses.get(productKey) ? 'connected' : 'default');
	}, [productStatuses, productKey]);

	const checkTextOverflow = useCallback(() => {
		if (textRef.current) {
			const element = textRef.current;
			return element.scrollWidth > element.clientWidth;
		}
		return false;
	}, []);

	const { onAuthorize } = useOAuthHandlers({
		product: productKey,
		successFlagTitle: formatMessage(messages.authorizationSuccess, { product: productDisplayName }),
		failFlagTitle: formatMessage(messages.authorizationFail, { product: productDisplayName }),
		outboundAuthUrl: url,
		onCompletedAction: () => {
			setCurConnectStatus('connected');
			setProductStatus(productKey, true);
			setProductConnectedInSession(true);
		},
		onFailAction: () => {
			setCurConnectStatus('error');
		},
		isOnSearchDialog,
		isBulkConnect: true,
	});

	const onConnectClick = () => {
		onAuthorize(oAuthSource);
	};
	let ProductIcon = icon;
	let connectAction;
	switch (curConnectStatus) {
		case 'connected':
			connectAction = (
				<Inline space="space.050" alignBlock="center" xcss={connectedLinkStyles}>
					<CheckMarkIcon
						label={formatMessage(messages.checkIconLabel)}
						color={token('color.icon.accent.green')}
					/>
					<Box>
						<FormattedMessage {...messages.connected} />
					</Box>
				</Inline>
			);
			break;
		case 'error':
			connectAction = (
				<Pressable
					onClick={onConnectClick}
					xcss={connectPressableStyles}
					backgroundColor="color.background.neutral.subtle"
				>
					<Inline alignBlock="center">
						<Inline space="space.050" alignBlock="center">
							<StatusErrorIcon
								label={formatMessage(messages.errorIconLabel)}
								color={token('color.icon.danger')}
							/>
							<Box xcss={errorMessageStyles}>
								<FormattedMessage {...messages.failed} />
							</Box>
						</Inline>
						<Box xcss={errorLinkStyles}>
							<FormattedMessage {...messages.retry} />
						</Box>
					</Inline>
				</Pressable>
			);
			break;
		default:
			connectAction = (
				<Pressable
					onClick={onConnectClick}
					xcss={connectPressableStyles}
					backgroundColor="color.background.neutral.subtle"
				>
					<Box xcss={connectLinkStyles}>
						<Inline space="space.050" alignBlock="center">
							<Box xcss={connectIconStyles}>
								<LinkExternalIcon
									label={formatMessage(messages.externalLinkIconlabel)}
									size="small"
								/>
							</Box>
							<Box>
								<FormattedMessage {...messages.connect} />
							</Box>
						</Inline>
					</Box>
				</Pressable>
			);
	}

	return (
		<Box xcss={tileStyles}>
			<Box xcss={contentStyles}>
				<Stack space="space.075" alignInline="center">
					<Box xcss={iconStyles}>{<ProductIcon />}</Box>
					<Inline xcss={productTitleStyles} space="space.050" alignBlock="center">
						<Tooltip content={productDisplayName} canAppear={checkTextOverflow}>
							<Box
								xcss={[titleTextStyles, newlyConnected && newProductTitleTextWidth]}
								ref={textRef}
							>
								{productDisplayName}
							</Box>
						</Tooltip>
						{newlyConnected && (
							<Inline alignInline="center">
								<Tag
									text={formatMessage(messages.new)}
									color="purple"
									isRemovable={false}
									migration_fallback="lozenge"
								/>
							</Inline>
						)}
					</Inline>
					{connectAction}
				</Stack>
			</Box>
		</Box>
	);
};
